import { useCallback } from 'react';
import { useRouter } from '@tanstack/react-router';

import { downloadBlobAsFile } from '@libs/file';
import { useErrorNotification } from '@providers/ErrorsNotificationProvider';

import { TournamentCardPageProps } from '.';

import { useClubRoles } from '@features/auth/hooks/useClubRoles';
import { useIsAdmin } from '@features/auth/hooks/useIsAdmin';
import { useClubCardQuery } from '@features/club/api/hooks';
import {
  usePauseTournamentMutation,
  usePublishTournamentMutation,
  useResumeTournamentMutation,
  useTournamentDownloadRcfReportMutation,
  useTournamentRcfReportAvailabilityQuery,
} from '@features/tournament/api/hooks';
import { useTournamentStates } from '@features/tournament/hooks/useTournamentStates';
import { useTournamentCtx } from '@features/tournament/providers/TournamentProvider';
import { NoPairModalWidget } from '@features/tournament/ui/components/NoPairModal/NoPairModalWidget';
import { TournamentGamesWidget } from '@features/tournament/ui/components/TournamentGames';
import { TournamentParticipantsMegaWidget } from '@features/tournament/ui/components/TournamentParticipantsMegaWidget/TournamentParticipantsMegaWidget';
import { TournamentParticipationButtonWidget } from '@features/tournament/ui/components/TournamentParticipationButton/TournamentParticipationButtonWidget';

import { TournamentCardPage } from './TournamentCardPage';

export function TournamentCardPageWidget({
  resultsTab,
  onResultsTabChange,
}: {
  resultsTab: 'table' | 'rounds' | 'list' | 'teams';
  onResultsTabChange: (t: 'table' | 'rounds' | 'list' | 'teams') => void;
}) {
  const ctx = useTournamentCtx();
  const { state } = ctx;
  const { tournament, clubId, tournamentId } = state;

  const router = useRouter();
  const { report } = useErrorNotification();
  const { data: club, isPending: isClubPending } = useClubCardQuery(clubId, {
    staleTime: Infinity,
  });

  const { isManager } = useClubRoles(clubId);
  const isAdmin = useIsAdmin();

  const { mutateAsync: publishTournament, isPending: isPublishing } = usePublishTournamentMutation(
    clubId,
    tournamentId,
  );

  const { mutateAsync: pauseTournament, isPending: isPausing } = usePauseTournamentMutation(
    clubId,
    tournamentId,
  );

  const { mutateAsync: resumeTournament, isPending: isResuming } = useResumeTournamentMutation(
    clubId,
    tournamentId,
  );

  const onEdit = () => {
    router.navigate({
      to: '/club/$clubId/tournament/$tournamentId/edit',
      params: { clubId, tournamentId },
      from: '/',
    });
  };

  const onPublish = useCallback(async () => {
    const result = await publishTournament();
    if (!result) report();
  }, [publishTournament, report]);

  const onPause = useCallback(() => {
    pauseTournament();
  }, [pauseTournament]);

  const onResume = useCallback(() => {
    resumeTournament();
  }, [resumeTournament]);

  const isPending = isClubPending || state.isLoading;

  const { isPublishable, isFinished } = useTournamentStates(tournament ?? null);

  const { data: rcfAvailability } = useTournamentRcfReportAvailabilityQuery(
    clubId,
    tournamentId,
    isManager && isFinished,
  );

  const { mutateAsync: downloadRcfReport } = useTournamentDownloadRcfReportMutation(
    clubId,
    tournamentId,
  );

  const onRcfReportClick = useCallback(async () => {
    const blob = await downloadRcfReport();
    if (!blob) return;
    downloadBlobAsFile('report', blob);
  }, [downloadRcfReport]);

  const props: Omit<TournamentCardPageProps, 'games' | 'participants'> = {
    club,
    tournament,
    isPending,
    isManager,
    isAdmin,
    isPublishable,
    isPublishing,
    isPausing,
    isResuming,
    onEdit,
    onPublish,
    onPause,
    onResume,
    isRcfReportAvailable: !!rcfAvailability?.isAvailable,
    rcfReportError: rcfAvailability?.errors?.[0],
    onRcfReportClick,
  };

  return (
    <TournamentCardPage
      {...props}
      participants={
        <TournamentParticipantsMegaWidget
          resultsTab={resultsTab}
          onResultsTabChange={onResultsTabChange}
        />
      }
      games={<TournamentGamesWidget />}
      noPairModal={<NoPairModalWidget tournamentId={ctx.state.tournamentId} />}
      participationButton={<TournamentParticipationButtonWidget />}
    />
  );
}
